<script setup lang="ts">
    import TeaserStoryArrow from './arrow/TeaserStoryArrow.vue';
    import { gsap } from 'gsap';
    import { ScrollTrigger } from 'gsap/ScrollTrigger';
    import StoryblokImage from '~/storyblok/Image/utils/StoryblokImage.vue';

    export type GradientTheme =
        'standard' | 'orange-light' | 'blue-light' | 'red-light' | 'green-light' | 'purple-light' | 'beige-light';

    const props = defineProps<{
        blok: TeaserStoryStepStoryblok;
        storyIndex: number;
        storyLength?: number;
        reverse?: boolean;
        hideNumbers?: boolean;
        hideArrows?: boolean;
        theme?: string;
        flipped?: boolean;
    }>();

    gsap.registerPlugin(ScrollTrigger);

    const gradientTheme = ref<GradientTheme>('standard');

    switch (props.theme) {
        case 'orange':
            gradientTheme.value = 'orange-light';
            break;
        case 'blue':
            gradientTheme.value = 'blue-light';
            break;
        case 'red':
            gradientTheme.value = 'red-light';
            break;
        case 'green':
            gradientTheme.value = 'green-light';
            break;
        case 'purple':
            gradientTheme.value = 'purple-light';
            break;
        case 'beige':
            gradientTheme.value = 'beige-light';
            break;
    }

    const hasTextContent = !!props.blok.text?.content?.[0]?.content;

    let isVideo = false;
    const fileType = props.blok.media?.filename?.split('.').pop();

    if (fileType === 'mp4' || fileType === 'webm' || fileType === 'wmv' || fileType === 'mov') {
        isVideo = true;
    }

    const step = ref();
    const media = ref();
    const number = ref();
    const text = ref();
    const shape = ref();
    const mediaMatch = gsap.matchMedia();
    const startPointValue = '0% 60%';
    const endPointValue = 'bottom bottom';

    onMounted(() => {
        mediaMatch.add('(max-width: 1023px)', () => {
            if (props.blok.media?.filename) {
                gsap.from(media.value, {
                    scrollTrigger: {
                        trigger: step.value,
                        scrub: 0.8,
                        start: startPointValue,
                        end: endPointValue,
                        toggleActions: 'restart pause reverse pause'
                    },
                    y: 120
                });
            }

            if (!props.blok.media?.filename) {
                gsap.from(text.value, {
                    scrollTrigger: {
                        trigger: step.value,
                        scrub: 0.8,
                        start: startPointValue,
                        end: endPointValue,
                        toggleActions: 'restart pause reverse pause'
                    },
                    y: 120
                });
            }

            if (!props.hideNumbers) {
                gsap.from(number.value, {
                    scrollTrigger: {
                        trigger: step.value,
                        scrub: 0.8,
                        start: startPointValue,
                        end: endPointValue,
                        toggleActions: 'restart pause reverse pause'
                    },
                    scale: 0.6,
                    opacity: 0
                });
            }
        });

        mediaMatch.add('(min-width: 1024px)', () => {
            if (props.blok.media?.filename && props.storyIndex !== 1) {
                gsap.from(media.value, {
                    scrollTrigger: {
                        trigger: step.value,
                        scrub: 0.8,
                        start: startPointValue,
                        end: endPointValue,
                        toggleActions: 'restart pause reverse pause'
                    },
                    y: 120
                });
            }

            if (!props.hideNumbers) {
                gsap.from(number.value, {
                    scrollTrigger: {
                        trigger: step.value,
                        scrub: 0.8,
                        start: startPointValue,
                        end: endPointValue,
                        toggleActions: 'restart pause reverse pause'
                    },
                    scale: 0.6,
                    opacity: 0
                });
            }

            gsap.from(shape.value, {
                scrollTrigger: {
                    trigger: step.value,
                    scrub: 2,
                    start: startPointValue,
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: 50,
                x: props.blok.step === '2' ? -120 : 120,
                opacity: 0
            });

            if (hasTextContent) {
                if (props.blok.media?.filename) {
                    gsap.from(text.value, {
                        scrollTrigger: {
                            trigger: step.value,
                            scrub: 0.8,
                            start: startPointValue,
                            end: endPointValue,
                            toggleActions: 'restart pause reverse pause'
                        },
                        y: 50,
                        x: props.blok.step === '2' ? -90 : 90,
                        opacity: 0
                    });
                } else {
                    gsap.from(text.value, {
                        scrollTrigger: {
                            trigger: step.value,
                            scrub: 0.8,
                            start: startPointValue,
                            end: endPointValue,
                            toggleActions: 'restart pause reverse pause'
                        },
                        y: 120
                    });
                }
            }
        });
    });
</script>

<template>
    <div
        ref="step"
        v-editable="blok"
        class="step"
        :class="{
            '--no-media': !blok.media?.filename,
            '--reverse': reverse,
            '--single-element': (hasTextContent && !blok.media?.filename) || (!hasTextContent && blok.media?.filename),
            '--theme-orange': theme === 'orange',
            '--theme-blue': theme === 'blue',
            '--theme-red': theme === 'red',
            '--theme-green': theme === 'green',
            '--theme-purple': theme === 'purple',
            '--theme-beige': theme === 'beige'
        }"
    >
        <div
            v-if="blok.media?.filename"
            ref="media"
            class="media"
            :class="{ '--no-text': !hasTextContent }"
        >
            <!-- <img
                v-if="!isVideo"
                :src="blok.media.filename"
                :alt="blok.media.alt"
            /> -->
            <StoryblokImage
                v-if="!isVideo && blok.media?.filename"
                :media="blok.media"
                type="mainPicture"
                :alt="blok.media.alt ? blok.media.alt : ''"
            />
            <video
                v-else-if="isVideo && blok.media?.filename"
                :src="blok.media.filename"
                :alt="blok.media.alt"
                autoplay
                muted
                loop
                playsinline
                webkit-playsinline="true"
                controlsList="nodownload"
                preload="metadata"
            >
                <source
                    :src="blok.media.filename + '#t=0.1'"
                    :type="'video/' + fileType"
                />
            </video>

            <span
                v-if="!hideNumbers"
                ref="number"
                class="number"
                aria-hidden="true"
                >{{ storyIndex }}</span
            >
        </div>

        <div
            v-if="hasTextContent"
            ref="text"
            class="text"
        >
            <h2 v-if="blok.title">{{ blok.title }}</h2>

            <RichText :doc="blok.text" />

            <span
                v-if="!blok.media?.filename && !hideNumbers"
                ref="number"
                class="number"
                aria-hidden="true"
                >{{ storyIndex }}</span
            >
        </div>

        <div
            ref="shape"
            class="background"
        >
            <GradientShape
                :shape="reverse ? 'leaf' : 'circle'"
                :theme="gradientTheme"
                :gradient-angle="reverse ? '-90deg' : '0deg'"
            />
        </div>

        <TeaserStoryArrow
            v-if="storyIndex != storyLength && !hideArrows"
            :story-index="storyIndex"
            :reverse="reverse"
            :theme="theme"
            :has-text="hasTextContent"
            :flipped="flipped"
        />
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/teaser/teaser-story-step.scss';
</style>
